import { LOAI_DINH_MUC } from "@/core/constants/const";
import { computed, type Ref } from "vue";
import type { RouteLocationNormalizedLoaded } from "vue-router";
import { PHIEU_XUAT_KHO_LOAI } from "../constants";
import type { PhieuXuatKhoLoaiPhieu } from "../types";

export type PhieuXuatKhoFormMode = "create" | "edit";

export interface PhieuXuatKhoFormConfig {
  loaiPhieu: PhieuXuatKhoLoaiPhieu;
  mode: PhieuXuatKhoFormMode;
  isBbgt: boolean;
  isManual: boolean;
  pageTitle: string;
  documentTitleSuffix: string;
  defaultLyDo: string;
  showLyDoColumn: boolean;
  showTaiKhoan: boolean;
  showThuHoiColumns: boolean;
  submitCreateMessage: string;
  submitUpdateMessage: string;
  confirmCreateTitle: string;
  confirmUpdateTitle: string;
}

const ROUTE_CONFIG: Record<
  string,
  Pick<
    PhieuXuatKhoFormConfig,
    | "loaiPhieu"
    | "mode"
    | "pageTitle"
    | "documentTitleSuffix"
    | "defaultLyDo"
    | "showLyDoColumn"
    | "showTaiKhoan"
    | "submitCreateMessage"
    | "submitUpdateMessage"
    | "confirmCreateTitle"
    | "confirmUpdateTitle"
  >
> = {
  "tao-moi-phieu-xuat-kho": {
    loaiPhieu: PHIEU_XUAT_KHO_LOAI.BBGT,
    mode: "create",
    pageTitle: "Tạo mới PXK",
    documentTitleSuffix: "Phiếu xuất kho",
    defaultLyDo: "",
    showLyDoColumn: false,
    showTaiKhoan: false,
    submitCreateMessage: "Tạo phiếu xuất thành công",
    submitUpdateMessage: "",
    confirmCreateTitle: "Tạo mới phiếu xuất kho với những thông tin đã nhập?",
    confirmUpdateTitle: "",
  },
  "tao-phieu-xuat-kho-khac": {
    loaiPhieu: PHIEU_XUAT_KHO_LOAI.TAM_XUAT,
    mode: "create",
    pageTitle: "Tạo mới phiếu xuất khác",
    documentTitleSuffix: "Phiếu xuất khác",
    defaultLyDo: "Phiếu xuất kho khác",
    showLyDoColumn: false,
    showTaiKhoan: true,
    submitCreateMessage: "Tạo phiếu xuất khác thành công",
    submitUpdateMessage: "Cập nhật phiếu xuất khác thành công",
    confirmCreateTitle:
      "Xác nhận tạo phiếu xuất khác với những thông tin đã nhập?",
    confirmUpdateTitle: "Xác nhận cập nhật phiếu xuất khác?",
  },
  "cap-nhat-phieu-xuat-kho-khac": {
    loaiPhieu: PHIEU_XUAT_KHO_LOAI.TAM_XUAT,
    mode: "edit",
    pageTitle: "Phiếu xuất khác",
    documentTitleSuffix: "Phiếu xuất khác",
    defaultLyDo: "Phiếu xuất khác",
    showLyDoColumn: false,
    showTaiKhoan: true,
    submitCreateMessage: "",
    submitUpdateMessage: "Cập nhật phiếu xuất khác thành công",
    confirmCreateTitle: "",
    confirmUpdateTitle: "Xác nhận cập nhật phiếu xuất khác?",
  },
  "tao-phieu-xuat-kho-thu-hoi": {
    loaiPhieu: PHIEU_XUAT_KHO_LOAI.THU_HOI,
    mode: "create",
    pageTitle: "Tạo mới phiếu xuất vật tư thu hồi",
    documentTitleSuffix: "Phiếu xuất vật tư thu hồi",
    defaultLyDo: "Phiếu xuất kho vật tư thu hồi",
    showLyDoColumn: true,
    showTaiKhoan: true,
    submitCreateMessage: "Tạo phiếu xuất kho vật tư thu hồi thành công",
    submitUpdateMessage: "Cập nhật phiếu xuất kho vật tư thu hồi thành công",
    confirmCreateTitle:
      "Bạn có chắc chắn muốn tạo phiếu xuất kho vật tư thu hồi với các thông tin đã nhập?",
    confirmUpdateTitle:
      "Bạn có chắc chắn muốn cập nhật phiếu xuất kho vật tư thu hồi với các thông tin đã nhập?",
  },
  "cap-nhat-phieu-xuat-kho-thu-hoi": {
    loaiPhieu: PHIEU_XUAT_KHO_LOAI.THU_HOI,
    mode: "edit",
    pageTitle: "Phiếu xuất vật tư thu hồi",
    documentTitleSuffix: "Phiếu xuất vật tư thu hồi",
    defaultLyDo: "Phiếu xuất kho vật tư thu hồi",
    showLyDoColumn: true,
    showTaiKhoan: true,
    submitCreateMessage: "",
    submitUpdateMessage: "Cập nhật phiếu xuất kho vật tư thu hồi thành công",
    confirmCreateTitle: "",
    confirmUpdateTitle:
      "Bạn có chắc chắn muốn cập nhật phiếu xuất kho vật tư thu hồi với các thông tin đã nhập?",
  },
  "cap-nhat-phieu-xuat-kho-theo-bien-ban": {
    loaiPhieu: PHIEU_XUAT_KHO_LOAI.BBGT,
    mode: "edit",
    pageTitle: "Chỉnh sửa phiếu xuất kho",
    documentTitleSuffix: "Phiếu xuất kho",
    defaultLyDo: "",
    showLyDoColumn: false,
    showTaiKhoan: false,
    submitCreateMessage: "",
    submitUpdateMessage: "Cập nhật phiếu xuất kho thành công",
    confirmCreateTitle: "",
    confirmUpdateTitle: "Xác nhận cập nhật phiếu xuất kho?",
  },
};

export function resolvePhieuXuatKhoFormConfig(
  route: RouteLocationNormalizedLoaded,
  loaiDinhMuc?: Ref<LOAI_DINH_MUC>
): PhieuXuatKhoFormConfig {
  const routeName = route.name?.toString() ?? "";
  const base =
    ROUTE_CONFIG[routeName] ?? ROUTE_CONFIG["tao-phieu-xuat-kho-khac"];

  const isBbgt = base.loaiPhieu === PHIEU_XUAT_KHO_LOAI.BBGT;

  let pageTitle = base.pageTitle;

  if (isBbgt && loaiDinhMuc?.value) {
    const suffix =
      loaiDinhMuc.value === LOAI_DINH_MUC.DINH_MUC_CO_DINH
        ? "(ĐMCĐ)"
        : "(Ngoài ĐM)";
    pageTitle =
      base.mode === "edit"
        ? `Chỉnh sửa PXK ${suffix}`
        : `Tạo mới PXK ${suffix}`;
  }

  return {
    ...base,
    pageTitle,
    isBbgt,
    isManual: !isBbgt,
    showThuHoiColumns: base.loaiPhieu !== PHIEU_XUAT_KHO_LOAI.THU_HOI,
  };
}

export function usePhieuXuatKhoFormMode(
  route: RouteLocationNormalizedLoaded,
  loaiDinhMuc?: Ref<LOAI_DINH_MUC>
) {
  const formConfig = computed(() =>
    resolvePhieuXuatKhoFormConfig(route, loaiDinhMuc)
  );

  const manualPageHeading = computed(() => {
    if (formConfig.value.loaiPhieu === PHIEU_XUAT_KHO_LOAI.BBGT) {
      if (loaiDinhMuc?.value === LOAI_DINH_MUC.NGOAI_DINH_MUC) {
        return "XUẤT VẬT TƯ NGOÀI ĐỊNH MỨC";
      }

      return "XUẤT VẬT TƯ TRONG ĐỊNH MỨC";
    }

    if (formConfig.value.loaiPhieu === PHIEU_XUAT_KHO_LOAI.THU_HOI) {
      return "XUẤT KHO VẬT TƯ THU HỒI";
    }

    return "XUẤT KHÁC VẬT TƯ";
  });

  return {
    formConfig,
    manualPageHeading,
  };
}
